import React, { useState, useCallback, useContext, useEffect, useRef } from 'react';
import { Typography, Select, DatePicker, Table, Tag, message, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../api';
import { UserContext } from '../App';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'Completed', value: 'completed'         },
  { label: 'Disputed',  value: 'rescore_requested' },
];

const STATUS_COLOR = {
  completed:         'green',
  rescore_requested: 'purple',
};

const STATUS_LABEL = {
  completed:         'Completed',
  rescore_requested: 'Disputed',
};

let _cache = null;

export default function MyErrorsPage() {
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);

  const defaultRange = [dayjs().subtract(3, 'month').startOf('month'), dayjs()];

  const restoredRef = useRef(!!_cache);
  const [rows,      setRows]      = useState(() => _cache?.rows      ?? []);
  const [loading,   setLoading]   = useState(false);
  const [dateRange, setDateRange] = useState(() => _cache?.dateRange ?? defaultRange);
  const [status,    setStatus]    = useState(() => _cache?.status    ?? []);

  const load = useCallback(async () => {
    if (!currentUser?.ID) return;
    setLoading(true);
    try {
      // my_errors pins the agent to the session user server-side. Passing an
      // id from here did nothing: /api/errors ignored it, so anyone whose role
      // was not 'agent' saw every error in their campaigns on this page.
      const params = { my_errors: '1' };
      params.status = status.length ? status.join(',') : 'completed,rescore_requested';
      if (dateRange?.[0]) params.start_date = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1]) params.end_date   = dateRange[1].format('YYYY-MM-DD');
      const res = await api.get('/api/errors', { params });
      setRows(res.data);
      _cache = { rows: res.data, dateRange, status };
    } catch {
      message.error('Failed to load error submissions.');
    } finally {
      setLoading(false);
    }
  }, [status, dateRange, currentUser?.ID]);

  useEffect(() => {
    if (restoredRef.current) { restoredRef.current = false; return; }
    load();
  }, [load]);

  const columns = [
    { title: 'ID',   dataIndex: 'ID',         key: 'ID',         width: 70 },
    { title: 'Date', dataIndex: 'CreateDate',  key: 'CreateDate', width: 110 },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 120,
      render: (v) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{STATUS_LABEL[v] ?? v}</Tag>,
    },
    { title: 'Agent',       dataIndex: 'AgentName',     key: 'AgentName',     width: 160, ellipsis: true },
    { title: 'Assigned To', dataIndex: 'AssignedToName', key: 'AssignedToName', width: 160, ellipsis: true },
    { title: 'Campaign', dataIndex: 'Campaign_Name', key: 'Campaign_Name', ellipsis: true },
    { title: 'Form',     dataIndex: 'FormName',      key: 'FormName',      ellipsis: true },
    {
      title: 'Total Errors', dataIndex: 'TotalErrors', key: 'TotalErrors', width: 110, align: 'right',
      render: (v) => v > 0 ? <Tag color="red">{v}</Tag> : <Tag>{v}</Tag>,
    },
    {
      title: 'Feedbacks', dataIndex: 'TotalFeedbacks', key: 'TotalFeedbacks', width: 95, align: 'right',
      render: (v) => v > 0 ? <Tag color="orange">{v}</Tag> : <Tag>{v}</Tag>,
    },
    {
      title: 'Ack Due', key: 'AckDue', width: 105,
      render: (_, r) => {
        if (r.Status !== 'completed' || !r.CompletedAt) return <Typography.Text type="secondary">—</Typography.Text>;
        if (r.AcknowledgedAt) return <Typography.Text type="secondary">Acknowledged</Typography.Text>;
        const dl = dayjs(r.CompletedAt).add(r.DisputeWindowDays ?? 4, 'day');
        const past = dayjs().isAfter(dl);
        return <span style={{ color: past ? '#8c8c8c' : '#fa8c16', fontWeight: past ? undefined : 600 }}>{dl.format('MM/DD/YY')}</span>;
      },
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>My Error Submissions</Typography.Title>

        <Space wrap>
          <RangePicker
            placeholder={['Date From', 'Date To']}
            value={dateRange}
            onChange={(v) => setDateRange(v ?? defaultRange)}
            format="YYYY-MM-DD"
          />
          <Select
            mode="multiple"
            allowClear
            placeholder="All statuses"
            options={STATUS_OPTIONS}
            value={status}
            onChange={setStatus}
            maxTagCount="responsive"
            style={{ minWidth: 160 }}
          />
        </Space>
      </div>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        scroll={{ x: 'max-content' }}
        pagination={{ defaultPageSize: 50, showSizeChanger: true, pageSizeOptions: ['25', '50', '100'], showTotal: (t) => `${t} submission${t === 1 ? '' : 's'}` }}
        onRow={(r) => ({ onClick: () => navigate(`/errors/${r.ID}`), style: { cursor: 'pointer' } })}
        locale={{ emptyText: 'No error submissions found.' }}
      />
    </>
  );
}
